/* PreventivoFamigliaConfermaPage.jsx — Pagina conferma post-submit
 * per Casa/Vita/Salute (riusabile).
 *
 * State route: { tracking_id, customer_id, auto_invite_status,
 *                debug_invite_token, prodotto }
 */
import { useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  CheckCircle2, ArrowRight, Sparkles, Home, Copy, Check,
} from "lucide-react";
import AccountCredentialsBox from "@/components/wizard/AccountCredentialsBox";

const PRODOTTO_META = {
  casa: { label: "Casa", emoji: "🏠" },
  vita: { label: "Vita", emoji: "🤝" },
  salute: { label: "Salute", emoji: "❤️" },
  viaggio: { label: "Viaggio", emoji: "✈️" },
  azienda: { label: "Azienda", emoji: "🏢" },
};

export default function PreventivoFamigliaConfermaPage() {
  const { state } = useLocation();
  const nav = useNavigate();
  const r = state || {};
  const prodotto = r.prodotto || "richiesta";
  const meta = PRODOTTO_META[prodotto] || { label: "Polizza", emoji: "📋" };
  const inviteToken = r.debug_invite_token;
  const inviteSent = r.auto_invite_status === "invite_sent";
  const [copied, setCopied] = useState(false);

  const copyTracking = async () => {
    if (!r.tracking_id) return;
    try {
      await navigator.clipboard.writeText(r.tracking_id);
      setCopied(true);
      toast.success("Tracking ID copiato");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-gradient-to-br from-emerald-50 via-white to-amber-50"
      data-testid="preventivo-conferma-page"
    >
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="max-w-2xl w-full bg-white rounded-3xl border border-emerald-200 shadow-2xl p-6 sm:p-10 text-center">
          <div className="mx-auto w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mb-5 ring-4 ring-emerald-200">
            <CheckCircle2 className="w-14 h-14 text-emerald-600" />
          </div>
          <p className="text-5xl mb-2" aria-hidden="true">{meta.emoji}</p>
          <h1 className="text-3xl sm:text-4xl font-black text-[#163321] mb-3"
              data-testid="conferma-title">
            Richiesta {meta.label} ricevuta!
          </h1>
          <p className="text-base text-slate-600 mb-5 leading-relaxed">
            Ti contatteremo entro <b>48 ore lavorative</b> con il tuo preventivo
            personalizzato. Senza impegno, senza pressioni.
          </p>

          {r.tracking_id && (
            <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-4 mb-5"
                 data-testid="conferma-tracking-card">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">
                Tracking ID
              </p>
              <div className="flex items-center justify-center gap-2">
                <code className="px-3 py-2 rounded-lg bg-white border border-slate-300 font-mono text-sm sm:text-base font-bold text-[#163321] break-all"
                       data-testid="conferma-tracking-id">
                  {r.tracking_id}
                </code>
                <button onClick={copyTracking}
                        data-testid="conferma-copy-tracking"
                        className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg bg-[#163321] text-white hover:bg-[#0e1f15]">
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}

          {inviteSent && (
            <div className="mt-6 rounded-2xl bg-gradient-to-br from-[#163321] to-[#0e1f15] text-white p-6 sm:p-7 text-left shadow-xl">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-amber-300" />
                <h2 className="text-xl sm:text-2xl font-black">
                  Crea Area Cliente in 30 secondi
                </h2>
              </div>
              <p className="text-emerald-100 text-sm sm:text-base mb-5 leading-relaxed">
                Segui lo stato del preventivo, vedi documenti e ricevi notifiche
                sull'avanzamento direttamente nella tua Area Cliente.
                I tuoi dati sono già pronti, ti basta una password.
              </p>
              <Link
                to={inviteToken
                  ? `/area-cliente/register?token=${inviteToken}`
                  : "/area-cliente/register"}
                data-testid="conferma-cta-area-cliente"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[60px] px-8 py-4 rounded-full bg-amber-400 text-[#163321] font-black text-base sm:text-lg shadow-lg hover:bg-amber-300 active:scale-[0.98] transition-all"
              >
                Crea Area Cliente <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          )}

          {/* FASE K2 — Credenziali account cliente auto-creato (opt-in) */}
          <AccountCredentialsBox account={r.account_cliente} />

          <div className="flex flex-col sm:flex-row gap-2 mt-7 justify-center">
            <button onClick={() => nav("/")}
                    data-testid="conferma-back-home"
                    className="min-h-[48px] px-5 py-3 rounded-full bg-slate-100 text-sm font-bold text-slate-700 hover:bg-slate-200 inline-flex items-center gap-2 justify-center">
              <Home className="w-4 h-4" /> Torna alla home
            </button>
          </div>

          {r.otp_unverified && (
            <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5"
               data-testid="conferma-otp-warning">
              ⚠️ Telefono non verificato via OTP. La richiesta verrà controllata
              manualmente dal nostro team prima di procedere.
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
